import { SERVICE_MODES, type ServiceMode } from '../enums';
import type { ReportSpecificFilter, ReportTableColumn } from '../report-runtime';

export interface ServiceFrequencyTodayFilters {
  /** Expected Bangkok day on requests; frozen resolved day in saved snapshots. */
  businessDate?: string;
  serviceMode?: ServiceMode[];
  isFree?: boolean;
}

export interface ServiceFrequencyTodayRow {
  providerId: string;
  providerName: string;
  businessDate: string;
  dateBasis: 'SERVICE_DATE';
  scopeProvenance: 'CURRENT_SERVICE_GROUP';
  scheduledDateTripCount: number;
  createdDateFallbackTripCount: number;
  requestsCount: number;
  acceptedTripCount: number;
  completedTripCount: number;
  cancelledTripCount: number;
  missedTripCount: number;
  activeAgentCount: number;
  activeAgentBasis: 'ACCEPTED_ASSIGNMENT_IN_SERVICE_COHORT';
  currency: 'THB';
  fareTotalSum: number;
  billedTotalSum: number;
  receivedTotalSum: number;
  outstandingTotalSum: number;
  freeTripCount: number;
  financeReviewCount: number;
  missingFareCount: number;
  missingAcceptedEvidenceCount: number;
  missingCompletedEvidenceCount: number;
  missingAgentEvidenceCount: number;
}

export const serviceFrequencyTodaySpecificFilters = [
  { id: 'serviceMode', type: 'multi', values: SERVICE_MODES },
  { id: 'isFree', type: 'boolean' },
  { id: 'businessDate', type: 'text' },
] as const satisfies readonly ReportSpecificFilter[];

export const serviceFrequencyTodayDateBasis = 'SERVICE_DATE';
export const serviceFrequencyTodaySorts = ['providerName'] as const;

function column(
  id: string,
  type: ReportTableColumn['type'],
  source: string,
  emptyMeaning = 'sftEmptyZero',
): ReportTableColumn {
  return {
    id,
    type,
    labelKey: `sftColumn_${id}`,
    source,
    emptyMeaning,
    ...(id === 'provider_id' || id === 'provider_name' ? {} : { meaningKey: source }),
    ...(id === 'provider_name' ? { sortable: true, sortKey: 'providerName' } : {}),
    ...(type === 'number' ? { unitKey: 'unit_count' } : {}),
  };
}

export const serviceFrequencyTodayColumns = [
  column('provider_id', 'text', 'sftSourceProvider', 'sftEmptyRequired'),
  column('provider_name', 'text', 'sftSourceProvider', 'sftEmptyRequired'),
  column('business_date', 'date', 'sftSourceFrozenDay', 'sftEmptyRequired'),
  column('date_basis', 'text', 'sftSourceServiceDate', 'sftEmptyRequired'),
  column('scope_provenance', 'text', 'sftSourceCurrentScope', 'sftEmptyRequired'),
  column('scheduled_date_trip_count', 'number', 'sftSourceScheduled'),
  column('created_date_fallback_trip_count', 'number', 'sftSourceFallback'),
  column('requests_count', 'number', 'sftSourceRequests'),
  column('accepted_trip_count', 'number', 'sftSourceAccepted'),
  column('completed_trip_count', 'number', 'sftSourceCompleted'),
  column('cancelled_trip_count', 'number', 'sftSourceCancelled'),
  column('missed_trip_count', 'number', 'sftSourceMissed'),
  column('active_agent_count', 'number', 'sftSourceActiveAgents'),
  column('active_agent_basis', 'text', 'sftSourceActiveAgents', 'sftEmptyRequired'),
  column('currency', 'text', 'sftSourceFinance', 'sftEmptyRequired'),
  column('fare_total_sum', 'money', 'sftSourceFare'),
  column('billed_total_sum', 'money', 'sftSourceBilled'),
  column('received_total_sum', 'money', 'sftSourceReceived'),
  column('outstanding_total_sum', 'money', 'sftSourceOutstanding'),
  column('free_trip_count', 'number', 'sftSourceFree'),
  column('finance_review_count', 'number', 'sftSourceReview'),
  column('missing_fare_count', 'number', 'sftSourceMissingFare'),
  column('missing_accepted_evidence_count', 'number', 'sftSourceMissingAccepted'),
  column('missing_completed_evidence_count', 'number', 'sftSourceMissingCompleted'),
  column('missing_agent_evidence_count', 'number', 'sftSourceMissingAgent'),
] as const satisfies readonly ReportTableColumn[];

export const serviceFrequencyTodayDefaultColumns = [
  'provider_name',
  'business_date',
  'requests_count',
  'accepted_trip_count',
  'completed_trip_count',
  'cancelled_trip_count',
  'missed_trip_count',
  'active_agent_count',
  'billed_total_sum',
  'received_total_sum',
  'outstanding_total_sum',
  'missing_fare_count',
] as const;

export const serviceFrequencyTodaySummary = [
  { field: 'tripCount', labelKey: 'sftSummaryTripCount', unit: 'count' },
  { field: 'acceptedTripCount', labelKey: 'sftColumn_accepted_trip_count', unit: 'count' },
  { field: 'completedTripCount', labelKey: 'sftColumn_completed_trip_count', unit: 'count' },
  { field: 'cancelledTripCount', labelKey: 'sftColumn_cancelled_trip_count', unit: 'count' },
  { field: 'missedTripCount', labelKey: 'sftColumn_missed_trip_count', unit: 'count' },
  { field: 'activeAgentCount', labelKey: 'sftColumn_active_agent_count', unit: 'count' },
  { field: 'fareTotalSum', labelKey: 'sftColumn_fare_total_sum', unit: 'money' },
  { field: 'billedTotalSum', labelKey: 'sftColumn_billed_total_sum', unit: 'money' },
  { field: 'receivedTotalSum', labelKey: 'sftColumn_received_total_sum', unit: 'money' },
  { field: 'outstandingTotalSum', labelKey: 'sftColumn_outstanding_total_sum', unit: 'money' },
  { field: 'freeTripCount', labelKey: 'sftColumn_free_trip_count', unit: 'count' },
  { field: 'financeReviewCount', labelKey: 'sftColumn_finance_review_count', unit: 'count' },
  { field: 'missingFareCount', labelKey: 'sftColumn_missing_fare_count', unit: 'count' },
] as const;
